import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { TextField, Checkbox, FormControlLabel, Box, Typography, Link } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { BORDERED_BOX_SX, filterByLabel } from './valueUtils';

interface CheckboxListPickerProps {
    values: FieldValue[];
    selectedValues: string[];
    onToggleOption: (optionName: string, checked: boolean) => void;
    onSetMany: (optionNames: string[], checked: boolean) => void;
    searchPlaceholder: string;
    selectAllLabel: string;
    deselectAllLabel: string;
    noOptionsLabel: string;
    selectedCountLabel: (count: number) => string;
}

const OPTION_HEIGHT = 36;
const OPTION_HEIGHT_WITH_PATH = 52;
const PATH_SEPARATOR = ' / ';
const SEARCH_DEBOUNCE_MS = 250;
const OVERSCAN_ROWS = 6;
const INITIAL_VISIBLE_ROWS = 5;
const ELLIPSIS_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

function OptionLabel({ label, twoLines }: { label: string; twoLines: boolean }) {
    if (!twoLines) {
        return <Typography variant="body2" title={label} sx={ELLIPSIS_SX}>{label}</Typography>;
    }
    const cut = label.lastIndexOf(PATH_SEPARATOR);
    const parent = cut > 0 ? label.slice(0, cut) : '';
    const leaf = cut > 0 ? label.slice(cut + PATH_SEPARATOR.length) : label;
    return (
        <Box title={label} sx={{ minWidth: 0, lineHeight: 1.25 }}>
            {parent && (
                <Typography variant="caption" sx={{ ...ELLIPSIS_SX, display: 'block', color: 'text.secondary' }}>
                    {parent}
                </Typography>
            )}
            <Typography variant="body2" sx={{ ...ELLIPSIS_SX, fontWeight: parent ? 600 : 400 }}>
                {leaf}
            </Typography>
        </Box>
    );
}

function useVirtualScroll() {
    const scrollBoxRef = useRef<HTMLDivElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const frameRef = useRef<number | null>(null);
    const latestScrollTopRef = useRef(0);

    useEffect(() => {
        const box = scrollBoxRef.current;
        if (!box || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => setViewportHeight(entry.contentRect.height));
        observer.observe(box);
        return () => observer.disconnect();
    }, []);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        latestScrollTopRef.current = e.currentTarget.scrollTop;
        if (frameRef.current !== null) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            setScrollTop(latestScrollTopRef.current);
        });
    };

    const scrollToTop = () => {
        if (scrollBoxRef.current) scrollBoxRef.current.scrollTop = 0;
        setScrollTop(0);
    };

    return { scrollBoxRef, scrollTop, viewportHeight, handleScroll, scrollToTop };
}

function CheckboxListPicker({
    values,
    selectedValues,
    onToggleOption,
    onSetMany,
    searchPlaceholder,
    selectAllLabel,
    deselectAllLabel,
    noOptionsLabel,
    selectedCountLabel,
}: CheckboxListPickerProps) {
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { scrollBoxRef, scrollTop, viewportHeight, handleScroll, scrollToTop } = useVirtualScroll();

    const handleSearchChange = (newInputValue: string) => {
        setInputValue(newInputValue);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(newInputValue);
            scrollToTop();
        }, SEARCH_DEBOUNCE_MS);
    };

    const filteredValues = useMemo(() => filterByLabel(values, search), [values, search]);
    const hasPaths = useMemo(() => values.some(option => option.label.includes(PATH_SEPARATOR)), [values]);
    const rowHeight = hasPaths ? OPTION_HEIGHT_WITH_PATH : OPTION_HEIGHT;

    const allFilteredSelected = filteredValues.length > 0 && filteredValues.every(option => selectedSet.has(option.name));
    const handleSelectAllClick = () => onSetMany(filteredValues.map(option => option.name), !allFilteredSelected);

    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN_ROWS);
    const visibleRowCount = Math.ceil((viewportHeight || rowHeight * INITIAL_VISIBLE_ROWS) / rowHeight) + OVERSCAN_ROWS * 2;
    const endIndex = Math.min(filteredValues.length, startIndex + visibleRowCount);

    const optionRowSx = {
        display: 'flex',
        height: rowHeight,
        minHeight: rowHeight,
        m: 0,
        px: 1,
        overflow: 'hidden',
        '& .MuiFormControlLabel-label': { minWidth: 0, flex: 1 },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%' }}>
            {values.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {selectedCountLabel(selectedValues.length)}
                        </Typography>
                        {filteredValues.length > 0 && (
                            <Link component="button" type="button" variant="caption" underline="hover" onClick={handleSelectAllClick} sx={{ fontWeight: 600 }}>
                                {allFilteredSelected ? deselectAllLabel : selectAllLabel}
                                {inputValue ? ` (${filteredValues.length})` : ''}
                            </Link>
                        )}
                    </Box>
                    <TextField
                        size="small"
                        placeholder={searchPlaceholder}
                        value={inputValue}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </>
            )}
            <Box
                ref={scrollBoxRef}
                onScroll={handleScroll}
                sx={{ ...BORDERED_BOX_SX, display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}
            >
                {filteredValues.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, py: 1 }}>
                        {noOptionsLabel}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ height: startIndex * rowHeight, flexShrink: 0 }} />
                        {filteredValues.slice(startIndex, endIndex).map(option => (
                            <FormControlLabel
                                key={option.name}
                                sx={optionRowSx}
                                control={
                                    <Checkbox
                                        checked={selectedSet.has(option.name)}
                                        onChange={(e) => onToggleOption(option.name, e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={<OptionLabel label={option.label} twoLines={hasPaths} />}
                            />
                        ))}
                        <Box sx={{ height: (filteredValues.length - endIndex) * rowHeight, flexShrink: 0 }} />
                    </>
                )}
            </Box>
        </Box>
    );
}

export default CheckboxListPicker;
